import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { SidebarNavItem, hasActiveChild } from './SidebarNavItem';
import { BrandLogo } from '@/components/common';
import { Button } from '@/components/ui';
import { ROUTES } from '@/constants';
import { appConfig } from '@/config';
import { useAuth } from '@/context/AuthContext';
import { cn } from '@/utils/cn';
import {
  LayoutDashboard,
  Wallet,
  ArrowDownLeft,
  ArrowUpRight,
  LineChart,
  ArrowLeftRight,
  Landmark,
  Users,
  Truck,
  Package,
  Tags,
  Shapes,
  Building2,
  FileText,
  FolderOpen,
  KeyRound,
  BarChart3,
  Settings,
  PanelLeftClose,
  PanelLeftOpen,
  X,
  BriefcaseBusiness,
  ClipboardList,
  Receipt,
  FileSignature,
  TrendingUp,
  Percent,
  Calculator,
  Handshake,
  ScrollText,
} from 'lucide-react';

const LG_UP = '(min-width: 1024px)';

const navItems = [
  { label: 'Dashboard', to: ROUTES.DASHBOARD, icon: LayoutDashboard },
  {
    label: 'Cadastros',
    icon: ClipboardList,
    children: [
      { label: 'Clientes', to: ROUTES.CLIENTS, icon: Users },
      { label: 'Fornecedores', to: ROUTES.SUPPLIERS, icon: Truck },
      { label: 'Produtos e serviços', to: ROUTES.PRODUCTS, icon: Package },
      { label: 'Categorias', to: ROUTES.CATEGORIES, icon: Tags },
      { label: 'Tipos', to: ROUTES.PRODUCT_TYPES, icon: Shapes },
      { label: 'Centros de custo', to: ROUTES.COST_CENTERS, icon: Building2 },
    ],
  },
  {
    label: 'Comercial',
    icon: Handshake,
    children: [
      { label: 'Orçamentos', to: ROUTES.QUOTES, icon: ScrollText },
    ],
  },
  {
    label: 'Financeiro',
    icon: Wallet,
    children: [
      { label: 'Contas a pagar', to: ROUTES.ACCOUNTS_PAYABLE, icon: ArrowUpRight },
      { label: 'Contas a receber', to: ROUTES.ACCOUNTS_RECEIVABLE, icon: ArrowDownLeft },
      { label: 'Contas bancárias', to: ROUTES.BANK_ACCOUNTS, icon: Landmark },
      { label: 'Fluxo de caixa', to: ROUTES.CASH_FLOW, icon: LineChart },
      { label: 'Movimentações', to: ROUTES.TRANSACTIONS, icon: ArrowLeftRight },
    ],
  },
  {
    label: 'Contratos',
    icon: FileSignature,
    children: [
      { label: 'Contratos', to: ROUTES.CONTRACTS, icon: FileSignature },
      { label: 'Reajustes', to: ROUTES.CONTRACT_ADJUSTMENTS, icon: TrendingUp },
      { label: 'Índices', to: ROUTES.CONTRACT_INDICES, icon: Percent },
    ],
  },
  {
    label: 'Fiscal',
    icon: Receipt,
    children: [
      { label: 'Faturamento', to: ROUTES.BILLING, icon: Calculator },
      { label: 'Notas fiscais', to: ROUTES.INVOICES, icon: FileText },
    ],
  },
  {
    label: 'Gestão',
    icon: BriefcaseBusiness,
    children: [
      { label: 'Documentos', to: ROUTES.DOCUMENTS, icon: FolderOpen },
      { label: 'Relatórios', to: ROUTES.REPORTS, icon: BarChart3 },
      { label: 'Senhas', to: ROUTES.PASSWORDS, icon: KeyRound },
    ],
  },
  { label: 'Configurações', to: ROUTES.SETTINGS, icon: Settings },
];

function resolveActiveGroup(pathname) {
  const active = navItems.find(
    (item) => Array.isArray(item.children) && hasActiveChild(pathname, item.children),
  );
  return active?.label ?? null;
}

function useIsLgUp() {
  const [matches, setMatches] = useState(() => window.matchMedia(LG_UP).matches);

  useEffect(() => {
    const media = window.matchMedia(LG_UP);
    const onChange = () => setMatches(media.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  return matches;
}

export function Sidebar({ collapsed, onToggleCollapse, mobileOpen, onCloseMobile }) {
  const location = useLocation();
  const { user } = useAuth();
  const isLgUp = useIsLgUp();
  const hasOrganization = Boolean(user?.organizationId);
  const visibleNavItems = hasOrganization
    ? navItems
    : navItems.filter((item) => item.to === ROUTES.SETTINGS);
  const [openGroup, setOpenGroup] = useState(() => resolveActiveGroup(location.pathname));
  const [flyoutLabel, setFlyoutLabel] = useState(null);
  const compact = collapsed && isLgUp;
  const iconSize = compact ? 22 : 18;

  useEffect(() => {
    const activeGroup = resolveActiveGroup(location.pathname);
    if (activeGroup) setOpenGroup(activeGroup);
  }, [location.pathname]);

  useEffect(() => {
    setFlyoutLabel(null);
  }, [location.pathname, compact]);

  const handleToggleGroup = (label) => {
    if (compact) {
      setFlyoutLabel((current) => (current === label ? null : label));
      return;
    }
    setOpenGroup((current) => (current === label ? null : label));
  };

  const handleCloseFlyout = () => {
    setFlyoutLabel(null);
  };

  return (
    <>
      <div
        className={cn(
          'fixed inset-0 z-40 bg-slate-950/50 lg:hidden',
          mobileOpen ? 'block' : 'hidden',
        )}
        onClick={onCloseMobile}
        aria-hidden
      />

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex h-dvh w-[var(--sidebar-width)] shrink-0 flex-col overflow-hidden bg-[var(--sidebar)] text-[var(--sidebar-foreground)] transition-all duration-200 lg:static lg:h-full',
          collapsed && 'lg:w-[var(--sidebar-collapsed-width)]',
          mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
        )}
        aria-label="Navegação principal"
      >
        <div className="shrink-0 border-b border-white/10">
          <div
            className={cn(
              'flex',
              compact
                ? 'mx-auto w-10 flex-col items-center gap-1 pt-3 pb-2'
                : 'h-[var(--header-height)] items-center justify-between gap-2 px-3',
            )}
          >
            <Link
              to={ROUTES.DASHBOARD}
              onClick={onCloseMobile}
              className={cn(
                'flex min-w-0 items-center rounded-[var(--radius)] outline-none focus-visible:ring-2 focus-visible:ring-white/40',
                compact ? 'h-10 w-10 justify-center' : 'flex-1',
              )}
              aria-label={appConfig.appName}
            >
              <BrandLogo
                variant={compact ? 'mark' : 'horizontal'}
                size={compact ? undefined : 'sm'}
                className={cn(
                  'shrink-0',
                  compact ? 'h-8 w-auto max-w-[2.75rem]' : 'max-h-8 object-left',
                )}
                alt=""
              />
            </Link>
            <div
              className={cn(
                'flex items-center',
                compact ? 'h-10 w-10 justify-center' : 'shrink-0 gap-1',
              )}
            >
              <Button
                variant="ghost"
                size="icon"
                className={cn(
                  'hidden text-[var(--sidebar-foreground)] hover:bg-[var(--sidebar-muted)] lg:inline-flex',
                  compact && 'h-10 w-10 shrink-0',
                )}
                onClick={onToggleCollapse}
                aria-label={collapsed ? 'Expandir menu' : 'Recolher menu'}
              >
                {collapsed ? (
                  <PanelLeftOpen size={iconSize} aria-hidden />
                ) : (
                  <PanelLeftClose size={iconSize} aria-hidden />
                )}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="text-[var(--sidebar-foreground)] hover:bg-[var(--sidebar-muted)] lg:hidden"
                onClick={onCloseMobile}
                aria-label="Fechar menu"
              >
                <X size={18} />
              </Button>
            </div>
          </div>
        </div>

        <nav
          className={cn(
            'sidebar-nav min-h-0 flex-1 overflow-y-auto overscroll-contain',
            compact ? 'sidebar-nav--collapsed flex flex-col items-center px-0 py-3' : 'p-3',
          )}
        >
          <div className={cn(compact ? 'flex w-10 flex-col items-center gap-1' : 'space-y-1')}>
            {visibleNavItems.map((item) => (
              <SidebarNavItem
                key={item.label}
                item={item}
                collapsed={compact}
                onNavigate={() => {
                  handleCloseFlyout();
                  onCloseMobile?.();
                }}
                open={compact ? flyoutLabel === item.label : openGroup === item.label}
                onToggle={handleToggleGroup}
                onCloseFlyout={handleCloseFlyout}
              />
            ))}
          </div>
        </nav>

        <div className={cn('shrink-0 border-t border-white/10 p-3', compact && 'px-0')}></div>
      </aside>
    </>
  );
}
